import React from 'react';
import styled from 'styled-components';
import { CandidateMetrics, OptimizationReport } from '../services/apiService';
import { useUnits } from '../units';

/**
 * Baseline vs. best (and runners-up) from the assistant's optimize_design /
 * compare_topologies tools, shown under its answer so the numbers behind the
 * recommendation are visible.
 */

const Card = styled.div`
  margin-top: 8px;
  border: 1px solid #dadce0;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px;
  font-size: 11px;
  color: #3c4043;
  white-space: normal;
`;

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

const Sub = styled.div`
  color: #5f6368;
  margin-bottom: 6px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    text-align: right;
    padding: 2px 4px;
    border-bottom: 1px solid #f1f3f4;
    white-space: nowrap;
  }
  th:first-child, td:first-child {
    text-align: left;
    white-space: normal;
  }
  th { color: #5f6368; font-weight: 500; }
`;

const Row = styled.tr<{ $best?: boolean }>`
  background: ${p => (p.$best ? '#e6f4ea' : 'transparent')};
  font-weight: ${p => (p.$best ? 600 : 400)};
`;

const Status = styled.span<{ $ok: boolean }>`
  color: ${p => (p.$ok ? '#188038' : '#c5221f')};
`;

const Note = styled.div`
  margin-top: 6px;
  color: #5f6368;
`;

function describe(c: CandidateMetrics, fallback: string, singleKey?: string): string {
  if (c.layout) return c.layout;
  if (c.settings && Object.keys(c.settings).length) {
    if (singleKey) return c.settings[singleKey] ?? fallback;
    return Object.entries(c.settings).map(([k, v]) => `${k}: ${v}`).join(', ');
  }
  return fallback;
}

/** When every candidate varies the same single setting, name it once in the header. */
function sharedSingleKey(candidates: CandidateMetrics[]): string | undefined {
  const keys = new Set<string>();
  for (const c of candidates) {
    if (!c.settings) continue;
    const own = Object.keys(c.settings);
    if (own.length !== 1) return undefined;
    keys.add(own[0]);
  }
  return keys.size === 1 ? Array.from(keys)[0] : undefined;
}

export const OptimizationReportCard: React.FC<{ report: OptimizationReport }> = ({ report }) => {
  const units = useUnits();
  const fmt = (v: number | undefined, dim: Parameters<typeof units.display>[1]) =>
    v === undefined || v === null ? '—' : units.display(v, dim);

  const others = (report.ranking ?? report.top ?? [])
    .filter(c => describe(c, '') !== describe(report.best, ''))
    .slice(0, 4);
  const rows: { label: string; c: CandidateMetrics; best?: boolean }[] = [
    { label: 'Current', c: report.baseline },
    { label: 'Best', c: report.best, best: true },
    ...others.map(c => ({ label: '', c })),
  ];
  const count = report.evaluations ?? report.evaluated;
  const singleKey = sharedSingleKey([report.best, ...others]);

  return (
    <Card data-testid="optimization-report">
      <Title>{report.ranking ? 'Layout comparison' : 'Design optimisation'}</Title>
      <Sub>
        {report.objective_description}
        {count !== undefined && ` · ${count} simulated`}
        {report.stopped_early && ` · stopped early (${report.stopped_early})`}
      </Sub>
      <Table>
        <thead>
          <tr>
            <th>{singleKey || 'Design'}</th>
            <th title="Meets every temperature limit and constraint">OK</th>
            <th title="Worst margin to a temperature limit">Margin {units.unit('temperature_delta')}</th>
            <th>Peak {units.unit('temperature')}</th>
            <th>Flow {units.unit('flow')}</th>
            <th>Δp {units.unit('pressure')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label, c, best }, i) => (
            <Row key={i} $best={best}>
              <td>{label ? <strong>{label}: </strong> : null}{describe(c, label ? '' : '—', singleKey)}</td>
              <td><Status $ok={c.feasible}>{c.error ? 'error' : c.feasible ? '✓' : '✗'}</Status></td>
              <td>{fmt(c.min_margin, 'temperature_delta')}</td>
              <td>{fmt(c.peak_temperature, 'temperature')}</td>
              <td>{fmt(c.min_flow, 'flow')}</td>
              <td>{fmt(c.max_pressure_drop, 'pressure')}</td>
            </Row>
          ))}
        </tbody>
      </Table>
      {!report.best.feasible && report.best.violations && (
        <Note>Still violated: {report.best.violations.join(' ')}</Note>
      )}
      <Note>
        {report.applied ? 'The best design is included in the proposal below.' : 'The canvas was not changed.'}
        {report.note && !report.applied ? ` ${report.note}` : ''}
      </Note>
    </Card>
  );
};

export default OptimizationReportCard;
